"use client";

import { Transition, Dialog } from "@headlessui/react";
import { Fragment, useRef, useState } from "react";
import { type LeadershipSectionPeople_Fields } from "~/__generated__/graphql";
import CustomImage from "~/components/CustomImage";
import RichText from "~/components/RichText";

export default function PeopleCard({
  name,
  position,
  description,
  image,
  hideImageInPopup
}: LeadershipSectionPeople_Fields) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-col">
      <CustomImage
        data={image}
        className="aspect-[1.5] object-cover"
        sizes="(min-width: 1280px) calc(33.37vw - 158px), (min-width: 1040px) calc(33.64vw - 97px), (min-width: 640px) calc(50vw - 46px), calc(100vw - 48px)"
      />
      <div className="flex h-full flex-col gap-3.5 rounded-sm bg-white px-6 pb-8 pt-3.5">
        <p className="text-xl">{name}</p>
        <p className="flex-1 text-sm font-medium leading-snug">{position}</p>
        {description && (
          <RichText field={description} className="line-clamp-3" />
        )}
        <button
          onClick={() => setOpen(true)}
          className="h-14 w-40 rounded-sm bg-blue px-6 py-1 text-lg tracking-wide text-white"
        >
          Read More
        </button>
      </div>

      <Transition appear show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setOpen(false)}
          initialFocus={dialogRef ? dialogRef : undefined}
        >
          <div style={{ display: "none" }} ref={dialogRef} />
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-sm bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex flex-col gap-6 md:flex-row">
                    {!hideImageInPopup && (
                      <div className="h-auto min-h-[20rem] min-w-[16.5rem] max-w-full flex-1 lg:max-w-[38%]">
                        <CustomImage
                          data={image}
                          className="h-full min-h-[inherit] w-full object-cover object-left"
                          sizes="(min-width: 768px) calc(50vw - 48px), calc(100vw - 48px)"
                        />
                      </div>
                    )}
                    <div className="flex h-auto flex-1 flex-col justify-between gap-3.5 rounded-sm bg-white">
                      <div className="flex h-full flex-col gap-3.5">
                        <Dialog.Title className="text-xl">{name}</Dialog.Title>
                        <p className="text-sm font-medium leading-snug">
                          {position}
                        </p>
                        {description && <RichText field={description} />}
                      </div>
                      <div className="flex w-full justify-end">
                        <button
                          onClick={() => setOpen(false)}
                          className="h-14 w-24 rounded-sm border-[1px] border-blue bg-white px-6 py-1 text-lg tracking-wide text-blue"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
